import React, { useState, useCallback } from 'react';
import { styled } from '@compiled/react';
import { Field as AkField } from '@atlaskit/form';
import { colors } from '@atlaskit/theme';
// eslint-disable-next-line import/order
import { token } from '@atlaskit/tokens';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { usePopupController as useToggleController } from '@atlassian/jira-servicedesk-common/src/use-popup-controller';
import {
	HOLIDAY_PREFIX,
	NEW,
	EXISTING,
	NEW_AND_MODIFIED,
	EXISTING_AND_MODIFIED,
	HOLIDAY_VALIDATION_STATE_PREFIX,
} from '../../../../../../common/ui/constants.tsx';
import { Footer } from '../../../../../../common/ui/footer/index.tsx';
import { DateField } from './date';
import { Frequency } from './frequency';
import { Name } from './name';
import { ReadOnlyViewWithActions } from './read-only-view-with-actions';
import { getValidateFunction } from './utils';

type Props = {
	isRecurring: boolean;
	isNew: boolean;
	fieldId: number;
	defaultDate: string;
	defaultName: string;
	onRemove: (fieldId: number) => void;
	onSave: (fieldId: number) => void;
};

// Final form uses component node to extract the field state
// during edit holiday mode - App renders granular fields like date, name, date2, name2
// this hidden field holds the object and used on form submit.
export const HiddenFieldToMaintainState = AkField;

export const Holiday = ({
	fieldId,
	isRecurring,
	defaultDate,
	defaultName,
	isNew,
	onRemove,
	onSave,
}: Props) => {
	const [fieldState, setFieldState] = useState(isNew ? NEW : EXISTING);
	const [isEditing, onToggleEditing] = useToggleController(isNew);
	// go/jfe-eslint
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const onValidate = useCallback(getValidateFunction(fieldId), [fieldId]);

	const onCancel = () => {
		if (fieldState === NEW) {
			onRemove(fieldId);
		} else {
			onToggleEditing();
		}
	};
	const onSaveHoliday = () => {
		if (fieldState === NEW) {
			setFieldState(NEW_AND_MODIFIED);
		} else {
			setFieldState(EXISTING_AND_MODIFIED);
		}
		onSave(fieldId);
		onToggleEditing();
	};
	if (isEditing) {
		return (
			<EditViewContainer data-test-id="servicedesk-sla-settings.ui.calendars.editable.fields.holidays.holiday">
				<Row>
					<Name fieldId={fieldId} defaultName={defaultName} />
					<DateField fieldId={fieldId} defaultDate={defaultDate} />
					<Frequency fieldId={fieldId} isRecurring={isRecurring} />
				</Row>
				<AkField name={`${HOLIDAY_VALIDATION_STATE_PREFIX}${fieldId}`} validate={onValidate}>
					{({ valid }) => (
						<Footer isDisabled={!valid} isSecondary onCancel={onCancel} onSave={onSaveHoliday} />
					)}
				</AkField>
				{fieldState !== NEW && (
					<HiddenFieldToMaintainState
						name={`${HOLIDAY_PREFIX}${fieldId}`}
						defaultValue={{
							defaultName,
							defaultDate,
							isRecurring,
						}}
					>
						{() => null}
					</HiddenFieldToMaintainState>
				)}
			</EditViewContainer>
		);
	}

	return (
		<ReadOnlyViewWithActions
			fieldId={fieldId}
			defaultName={defaultName}
			defaultDate={defaultDate}
			isRecurring={isRecurring}
			onEditRequested={onToggleEditing}
			onRemove={() => onRemove(fieldId)}
		/>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const Row = styled.div({
	display: 'grid',
	gridTemplateColumns: 'repeat(3, 1fr)',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	gridGap: `${gridSize}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const EditViewContainer = styled.div({
	margin: `${token('space.100', '8px')} 0 ${token('space.100', '8px')} 0`,
	padding: token('space.100', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	boxShadow: token('elevation.shadow.raised', `0 1px 1px ${colors.N50A}, 0 0 1px ${colors.N60A}`),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	background: token('elevation.surface.raised', colors.N0),
});
